// Support chat — one thread per user (doc id = lowercased email) with a messages
// subcollection. Realtime via client Firestore SDK; attachments + email
// notifications go through the backend API (see server.js /api/support/chat/*).
import { auth, db } from '../firebase';
import { env } from '../config/environment';
import {
  collection,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';

const SUPPORT_CHATS_COLLECTION = 'supportChats';

export const supportChatThreadId = (email) => (email || '').trim().toLowerCase();

const mapThreadDoc = (snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null);

/** Subscribe to a single thread doc (unread counts, preview). */
export const subscribeToSupportChatThreadDoc = (threadEmail, callback) => {
  const id = supportChatThreadId(threadEmail);
  if (!id) return () => {};
  const ref = doc(db, SUPPORT_CHATS_COLLECTION, id);
  return onSnapshot(
    ref,
    (snap) => callback(mapThreadDoc(snap)),
    (error) => {
      console.error('Error subscribing to support chat thread:', error);
      callback(null);
    }
  );
};

/**
 * Subscribe to messages in a thread, oldest first. Includes metadata changes
 * so `pending` flips to false once a message commits (drives Sending → Delivered).
 */
export const subscribeToSupportChatMessages = (threadEmail, callback) => {
  const id = supportChatThreadId(threadEmail);
  if (!id) return () => {};
  const ref = collection(db, SUPPORT_CHATS_COLLECTION, id, 'messages');
  const q = query(ref, orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => callback(
      snapshot.docs.map((d) => ({ id: d.id, pending: d.metadata.hasPendingWrites, ...d.data() }))
    ),
    (error) => {
      console.error('Error subscribing to support chat messages:', error);
      callback([]);
    }
  );
};

/** Super admin inbox: all threads, most recent activity first. */
export const subscribeToAllSupportChats = (callback) => {
  const ref = collection(db, SUPPORT_CHATS_COLLECTION);
  const q = query(ref, orderBy('lastMessageAt', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (error) => {
      console.error('Error subscribing to support chats:', error);
      callback([]);
    }
  );
};

/**
 * Write a message and update thread denormalised fields atomically.
 * sender: 'user' | 'admin'. attachments: [{ fileId, fileName, contentType, size }]
 * threadUserName: display name for the thread owner — pass when an admin
 * messages first so the inbox shows a name before the user ever replies.
 */
export const sendSupportChatMessage = async ({
  threadEmail,
  sender,
  senderEmail,
  senderName,
  text,
  attachments = [],
  threadUserName,
}) => {
  const id = supportChatThreadId(threadEmail);
  if (!id) throw new Error('Thread email is required');
  const trimmed = (text || '').trim();
  if (!trimmed && attachments.length === 0) throw new Error('Message is empty');

  const threadRef = doc(db, SUPPORT_CHATS_COLLECTION, id);
  const messageRef = doc(collection(threadRef, 'messages'));
  const preview = trimmed
    ? trimmed.slice(0, 140)
    : attachments.length > 1
      ? `${attachments.length} screenshots attached`
      : 'Screenshot attached';

  const batch = writeBatch(db);
  batch.set(messageRef, {
    sender,
    senderEmail: supportChatThreadId(senderEmail),
    senderName: senderName || senderEmail || '',
    text: trimmed,
    attachments,
    createdAt: serverTimestamp(),
  });
  batch.set(
    threadRef,
    {
      userEmail: id,
      ...(sender === 'user'
        ? { userName: senderName || senderEmail || id }
        : threadUserName
          ? { userName: threadUserName }
          : {}),
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: preview,
      lastMessageSender: sender,
      ...(sender === 'user'
        ? { unreadForAdmin: increment(1) }
        : { unreadForUser: increment(1) }),
      // Sending a message ends the sender's typing state atomically.
      ...(sender === 'user' ? { userTypingAt: null } : { adminTypingAt: null }),
    },
    { merge: true }
  );
  await batch.commit();
};

/**
 * Typing heartbeat. Callers throttle while composing; a fresh timestamp
 * (see isSupportChatTypingFresh) shows the "…" bubble on the other side.
 * Fire-and-forget: failures only cost the indicator.
 */
export const setSupportChatTyping = async (threadEmail, viewer, isTyping) => {
  const id = supportChatThreadId(threadEmail);
  if (!id) return;
  const field = viewer === 'admin' ? 'adminTypingAt' : 'userTypingAt';
  try {
    await setDoc(
      doc(db, SUPPORT_CHATS_COLLECTION, id),
      { [field]: isTyping ? serverTimestamp() : null },
      { merge: true }
    );
  } catch (error) {
    console.warn('Support chat typing update failed:', error.message);
  }
};

/** Heartbeats older than this are treated as "stopped typing". */
export const SUPPORT_CHAT_TYPING_TTL_MS = 7000;

export const supportChatTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  const millis = new Date(value).getTime();
  return Number.isNaN(millis) ? 0 : millis;
};

export const isSupportChatTypingFresh = (typingAt, nowMillis = Date.now()) => {
  const millis = supportChatTimestampMillis(typingAt);
  return millis > 0 && nowMillis - millis < SUPPORT_CHAT_TYPING_TTL_MS;
};

/** Zero the viewer's unread count and record read time (drives email throttle reset). */
export const markSupportChatRead = async (threadEmail, viewer) => {
  const id = supportChatThreadId(threadEmail);
  if (!id) return;
  const threadRef = doc(db, SUPPORT_CHATS_COLLECTION, id);
  const patch =
    viewer === 'admin'
      ? { unreadForAdmin: 0, lastReadByAdminAt: serverTimestamp() }
      : { unreadForUser: 0, lastReadByUserAt: serverTimestamp() };
  try {
    await setDoc(threadRef, patch, { merge: true });
  } catch (error) {
    console.error('Error marking support chat read:', error);
  }
};

const getIdToken = async () => {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');
  return idToken;
};

/**
 * Ask the backend to email the other party. Throttled server-side
 * (one email per direction per thread per 10 minutes, reset on read).
 * direction: 'to-admin' | 'to-user'
 */
export const notifySupportChat = async (threadEmail, direction) => {
  const idToken = await getIdToken();
  const response = await fetch(env.apiEndpoints.supportChatNotify, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ threadEmail: supportChatThreadId(threadEmail), direction }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to send chat notification');
  }
  return response.json();
};

/** Upload an image attachment. Returns { fileId, fileName, contentType, size }. */
export const uploadSupportChatAttachment = async (file, threadEmail) => {
  const idToken = await getIdToken();
  const form = new FormData();
  form.append('file', file, file.name || 'screenshot.png');
  form.append('threadEmail', supportChatThreadId(threadEmail));
  const response = await fetch(env.apiEndpoints.supportChatAttachments, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
    body: form,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to upload attachment');
  }
  return response.json();
};

/**
 * Fetch an attachment as a blob (authenticated). thumbnail: true returns the
 * small WebP preview shown in the thread instead of the full image.
 */
export const fetchSupportChatAttachmentBlob = async (threadEmail, fileId, { thumbnail = false } = {}) => {
  const idToken = await getIdToken();
  const url = env.apiEndpoints.supportChatAttachment(supportChatThreadId(threadEmail), fileId);
  const response = await fetch(
    thumbnail ? `${url}?size=thumb` : url,
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Attachment not available');
  }
  return response.blob();
};
